import React, { useEffect, useState, useContext } from "react";
import { TextField, Popper, Paper, MenuItem } from "@mui/material";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { borderRadius, styled } from "@mui/system";
import { ThemeContext } from "../../theme/ThemeContext";
import CommonUtil from "../Util/CommonUtils";
import { Col } from "antd";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const CssTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== "darkMode",
})(({ theme, darkMode }) => ({
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderRadius: "8px",
      borderColor: darkMode ? "#576A7A" : "#ccc", // Default border color for the fieldset
    },
    "&:hover fieldset": {
      borderColor: darkMode ? "#fafafa" : "rgba(0, 0, 0, 0.4)", // Border color when hovered
    },
    "&.Mui-focused fieldset": {
      borderColor: "rgb(25, 118, 210)", // Border color when focused
    },
    "&.Mui-disabled fieldset": {
      borderColor: darkMode ? "#576A7A" : "#ccc", // Border color when disabled
    },
    "&.Mui-disabled": {
      opacity: 0.5,
      "-webkit-text-fill-color": darkMode ? "#fff" : "", // Disabled text color
    },
  },
  "& .MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
    color: darkMode ? "rgb(160, 158, 158)" : "rgb(0, 0, 0)",
  },
  "& .MuiInputLabel-shrink": {
    color: "red",
  },
  "& .MuiFormLabel-asterisk": {
    color: "#FE5050",
    opacity: "1",
    fontSize: "16px",
  },

  "& .MuiFormLabel-root": {
    fontSize: "0.875rem",
  },
  "& .MuiInputBase-input": {
    fontSize: "14px",
    color: darkMode ? "rgba(255, 255, 255, 1)" : "rgba(0, 0, 0, 1)", // Default text color
  },
  "& .MuiInputBase-input.Mui-disabled": {
    opacity: 1,
    "-webkit-text-fill-color": darkMode ? "#fff" : "rgba(0, 0, 0, 0.38)", // Disabled text color fix
  },
  "& input:-webkit-autofill": {
    "-webkit-box-shadow": `0 0 0 100px ${darkMode ? "#0F273B" : "#fff"
      } inset !important`, // Set the background color
    "-webkit-text-fill-color": darkMode ? "#fff !important" : "#000 !important", // Set the text color with !important to override defaults
    transition: "background-color 5000s ease-in-out 0s", // Prevent background flash
  },
  "& input:-webkit-autofill:hover": {
    "-webkit-text-fill-color": darkMode ? "#fff !important" : "#000 !important",
    "-webkit-box-shadow": `0 0 0 100px ${darkMode ? "#0F273B" : "#fff"
      } inset !important`,
  },
  "& input:-webkit-autofill:focus": {
    "-webkit-text-fill-color": darkMode ? "#fff !important" : "#000 !important",
    "-webkit-box-shadow": `0 0 0 100px ${darkMode ? "#0F273B" : "#fff"
      } inset !important`,
  },
  "& input:-webkit-autofill:active": {
    "-webkit-text-fill-color": darkMode ? "#fff !important" : "#000 !important",
    "-webkit-box-shadow": `0 0 0 100px ${darkMode ? "#0F273B" : "#fff"
      } inset !important`,
  },
}));

const CustomPopper = styled(Popper)(({ darkMode }) => ({
  "& .MuiAutocomplete-listbox": {
    backgroundColor: darkMode ? "#0F273B" : "#fff", // Dropdown background color
    color: darkMode ? "#fff" : "#000", // Dropdown text color
  },
  "& .MuiAutocomplete-option": {
    backgroundColor: darkMode ? "#0F273B" : "#fff", // Option background color
    "&[aria-selected='true']": {
      backgroundColor: darkMode ? "#0D1926" : "#e6f7ff", // Background color when an option is selected
    },
    "&:hover": {
      backgroundColor: darkMode ? "#0D1926" : "#e6f7ff", // Hover background color
    },
  },
}));

// Custom Paper component to apply styles to the "No Options found" text
const CustomPaper = styled(Paper)(({ darkMode }) => ({
  backgroundColor: darkMode ? "#0F273B" : "#fff", // Background color for the dropdown container
  color: darkMode ? "#fff" : "#000", // Text color for the "No Options found" text
}));

export default function CustomTextfield(props) {
  const [helperText, setHelperText] = useState(props.helperText);
  const [error, setError] = useState(props.error);
  const [value, setValue] = useState(props.value);
  const { t } = useTranslation();
  const { darkMode, disabled } = useContext(ThemeContext);

  useEffect(() => {
    setError(props.error);
    setHelperText(props.helperText);
  }, [props.error]);

  const handleChange = (e) => {
    var val = e.target.value;
    if (val.trim() === "") {
      val = val.trim();
    }
    if (e.target.value.length > 0 && val.length === 0) {
      return;
    }
    if (val.length > 2) {
      if (val[val.length - 1] === " " && val[val.length - 2] === " ") {
        return;
      }
      if (val[0] === " ") {
        return;
      }
    }
    if (props.periodValue === "") {
      setHelperText(t("Please select Subscription Type"));
      setError(true);
      return;
    }
    if (props.regex === "none" && props.validation) {
      switch (props.validation) {
        case "alpha-numeric":
          handleCode(e);
          break;
        case "numeric":
          handleNumeric(e);
          break;
        case "restrictLength":
          handleRestrictLength(e);
          break;
        case "email":
          handleEmail(e);
          break;
        case "password":
          handlePassword(e);
          break;
        case "code":
          handleCode(e);
          break;
        case "alphabets":
          handleAlphabets(e);
          break;
        case "restrictLengthWithAlphaNumeric":
          handleLengthWithAlphaNumeric(e);
          break;
        case "restrictNumericValueToTwoDigits":
          handleNumericWithTwoDigits(e);
          break;
        case "restrictNumericValueToTenDigits":
          handleNumericWithTenDigits(e);
          break;
        case "alphaNumeric":
          handleAlphaNumeric(e);
          break;
        case "numericWithSpace":
          handleNumericWithSpace(e);
          break;
        case "alphaNumericWithSpace":
          handleAlphaNumericWithSpace(e);
          break;
        case "subscriptionNumeric":
          handleNumericForSubscription(e);
          break;
        default:
          props.handleChange(e);
      }
    }
  };

  useEffect(() => {
    setValue(props.value);
  }, [props.value]);

  const handleAlphabets = (e) => {
    if (CommonUtil.isAlphabets(e.target.value)) {
      props.handleChange(e);
      setHelperText(props.helperText);
      setError(false);
    } else {
      setError(true);
      setHelperText(t("Only accepts alphabets"));
    }
  };

  const handleCode = (e) => {
    if (CommonUtil.isAlphaNumeric(e.target.value)) {
      props.handleChange(e);
      setHelperText(props.helperText);
      setError(false);
    } else {
      setError(true);
      setHelperText(t("Only accepts alphanumeric characters"));
    }
  };

  const handleNumeric = (e) => {
    if (CommonUtil.isValidNumeric(e.target.value)) {
      props.handleChange(e);
      setHelperText(props.helperText);
      setError(false);
    } else {
      setError(true);
      setHelperText(t("INVITATIONFORM019"));
    }
  };

  const handleRestrictLength = (e) => {
    if (e.target.value.length <= 100) {
      props.handleChange(e);
      setHelperText(props.helperText);
      setError(false);
    } else {
      setError(true);
      setHelperText(t("Characters length should be less than 100"));
    }
  };

  const handleLengthWithAlphaNumeric = (e) => {
    if (e.target.value.length > 100) {
      setError(true);
      setHelperText(t("Characters length should be less than 100"));
    } else if (!CommonUtil.isAlphaNumericWithSpace(e.target.value)) {
      setError(true);
      setHelperText(t("Only accepts alphanumeric characters"));
    } else {
      props.handleChange(e);
      setHelperText(props.helperText);
      setError(false);
    }
  };

  const handleAlphaNumeric = (e) => {
    if (!CommonUtil.isAlphaNumeric(e.target.value)) {
      setError(true);
      setHelperText(t("Only accepts alphanumeric characters"));
    } else {
      props.handleChange(e);
      setHelperText(props.helperText);
      setError(false);
    }
  };

  const handleNumericWithTwoDigits = (e) => {
    if (!CommonUtil.isValidNumeric(e.target.value)) {
      setError(true);
      setHelperText(t("ACCEPT_NUMBER"));
    } else if (e.target.value > 99) {
      setError(true);
      setHelperText(t("Discount should be less than 100%"));
    } else {
      props.handleChange(e);
      setHelperText(props.helperText);
      setError(false);
    }
  };

  const handleNumericWithSpace = (e) => {
    if (CommonUtil.isValidNumericWithSpace(e.target.value)) {
      props.handleChange(e);
      setHelperText(props.helperText);
      setError(false);
    } else {
      setError(true);
      setHelperText(t("ACCEPT_NUMBER"));
    }
  };

  const handleAlphaNumericWithSpace = (e) => {
    if (CommonUtil.isAlphaNumericWithSpace(e.target.value)) {
      props.handleChange(e);
      setHelperText(props.helperText);
      setError(false);
    } else {
      setError(true);
      setHelperText(t("ACCEPT_ALPHANUMERIC"));
    }
  };

  const handleNumericWithTenDigits = (e) => {
    if (!CommonUtil.isValidNumeric(e.target.value)) {
      setError(true);
      setHelperText(t("ACCEPT_NUMBER"));
    } else if (e.target.value.length > 10) {
      setError(true);
      setHelperText(
        t("Number of digits for the device should be limited to 10")
      );
    } else {
      props.handleChange(e);
      setHelperText(props.helperText);
      setError(false);
    }
  };

  const handleNumericForSubscription = (e) => {
    if (CommonUtil.isValidNumeric(e.target.value)) {
      props.handleChange(e);
      setHelperText(props.helperText);
      setError(false);
    } else {
      setError(true);
      setHelperText(t("INVITATIONFORM019"));
    }
    if (e.target.value.length <= 6) {
      props.handleChange(e);
      setHelperText(props.helperText);
      setError(false);
    } else {
      setError(true);
      setHelperText(t("NUMBER_VALIDATION_SUBSCRIPTION_PRICE"));
    }
  };

  const handleEmail = (e) => {
    props.handleChange(e);
    if (CommonUtil.isValidEmail(e.target.value)) {
      setHelperText(props.helperText);
      setError(false);
    } else {
      setError(true);
      setHelperText(t("INVITATIONFORM014"));
    }
  };

  const handlePassword = (e) => {
    props.handleChange(e);
    if (CommonUtil.isValidPassword(e.target.value)) {
      setHelperText(props.helperText);
      setError(false);
    } else {
      setError(true);
      setHelperText(t("Only accepts Password"));
    }
  };

  return (
    <CssTextField
      style={{ background: "transparent" }}
      autoComplete={props.autoComplete}
      {...props}
      required={props.required}
      disabled={props.disabled}
      value={props.value}
      helperText={helperText}
      error={error}
      onChange={(e) => handleChange(e)}
      // margin="normal"
      variant="outlined"
      fullWidth
      size="medium"
      type={props.type}
      label={props.label}
      name={props.name}
      onBlur={props.onBlur}
      className="login_input"
      // InputLabelProps={
      //   props.InputLabelProps ? { shrink: props.InputLabelProps } : undefined
      // }
      InputProps={{
        ...props.InputProps,
        style: {
          color: darkMode ? "#fff" : "#000",
          ...props.InputProps?.style,
        },
        endAdornment: props.subscription ? (
          <React.Fragment>
            {props.InputProps && props.InputProps.endAdornment}
            <TextField
              select
              value={"USD"}
              style={{ minWidth: "80px", marginRight: "-13px" }}
              variant="outlined"
              SelectProps={{
                IconComponent: ExpandMoreIcon,
              }}
            >
              <MenuItem value="USD">USD</MenuItem>
            </TextField>
          </React.Fragment>
        ) : null,
      }}
      InputLabelProps={{
        ...props.InputLabelProps,
        style: {
          color: darkMode ? "#bbb" : "#a09e9e", // Change label color based on dark mode
          ...props.InputLabelProps?.style,
        },
      }}
      PopperComponent={(props) => (
        <CustomPopper {...props} darkMode={darkMode} />
      )}
      PaperComponent={(props) => <CustomPaper {...props} darkMode={darkMode} />}
      darkMode={darkMode}
      // disabled={disabled}
      inputColor={props.inputColor}
    />
  );
}

CustomTextfield.defaultProps = {
  validation: "none",
  regex: "none",
  required: false,
  disabled: false,
  inputColor: null,
};

CustomTextfield.propTypes = {
  validation: PropTypes.oneOf([
    "alpha-numeric",
    "alphabets",
    "numeric",
    "restrictLength",
    "email",
    "password",
    "code",
    "restrictLengthWithAlphaNumeric",
    "restrictNumericValueToTwoDigits",
    "restrictNumericValueToTenDigits",
    "alphaNumeric",
    "numericWithSpace",
    "alphaNumericWithSpace",
    "subscriptionNumeric",
  ]),
  name: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  type: PropTypes.oneOf(["text", "number", "password"]),
  className: PropTypes.string,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  inputColor: PropTypes.string,
};
